import React, { useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useHistory, useLocation, withRouter } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { usePortfoliosLibraryColDefs } from './config/tableColumns';
import { ConditionalOverlay, DataGrid, UserContext } from 'venn-components';
import type { AgGridReact } from 'ag-grid-react';
import type {
  CellClickedEvent,
  GetRowIdParams,
  GridReadyEvent,
  RowSelectionOptions,
  SelectionChangedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import styled, { ThemeContext } from 'styled-components';
import { Button, ColorUtils, GetColor, LoadingSize, Spinner, ZIndex, getTextThemeProvider } from 'venn-ui-kit';
import { PAGE_LIMIT, TABLE_PAGE_SIZE } from './config/constants';
import { PortfoliosTableAnnotation } from './components/PortfoliosTableAnnotation';
import { PortfoliosTypeaheadSearchBar } from './components/PortfoliosTypeaheadSearchBar';
import { throttle } from 'lodash';
import { type PortfoliosLocationState, useHasFF } from 'venn-utils';
import {
  getPillDataFromFilterOptions,
  PORTFOLIO_STATUSES,
  type SortSettings,
  TAX_STATUSES,
  useClientNameLibraryFilterValues,
  useHouseHoldNameLibraryFilterValues,
  usePortfoliosLibraryData,
  useProgramLibraryFilterValues,
} from './logic/usePortfoliosLibraryData';
import type { LibrarySearchEntity } from 'venn-api';
import SimpleFilter from '../../../home-page/src/components/filters/components/SimpleFilter';
import { rowClassRules } from './config/tableRows';
import PortfoliosTableActions from './components/PortfoliosTableActions';
import { type FilterAction, FilterPillGroup, type FilterState } from './components/FilterPillGroup';

const NO_LOCAL_SORT = () => 0;

const DEFAULT_COL_DEF = {
  sortable: true,
  comparator: NO_LOCAL_SORT,
  resizable: false,
  lockPinned: true,
  suppressHeaderMenuButton: true,
  suppressMovable: true,
  editable: false,
  headerClass: 'ag-left-aligned-header',
};

const rowSelection: RowSelectionOptions = {
  mode: 'multiRow',
  selectAll: 'currentPage',
};

const bulkActionsComponent = <span>&npsp;</span>;

const rowStyle = { cursor: 'pointer' };
const viewportIsOverflowingClassName = 'viewport-is-overflowing';

const getRowId = (params: GetRowIdParams<LibrarySearchEntity>) =>
  params.data.portfolioId?.toString() ?? params.data.privatePortfolioId;

const emptyFilterState: FilterState = {
  household: [],
  client: [],
  program: [],
  status: [],
  taxStatus: [],
};

const initialFilterState: FilterState = {
  household: [],
  client: [],
  program: [],
  status: PORTFOLIO_STATUSES.filter((status) => status.value !== 'CLOSED').map(({ value }) => value),
  taxStatus: [],
};

const PortfoliosLibrary = () => {
  const hasTaxImpact = useHasFF('tax_impact_ff');

  const { Images } = useContext(ThemeContext);
  const { profileSettings } = useContext(UserContext);

  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [sortSettings, setSortSettings] = useState<SortSettings | undefined>();
  const clientNames = useClientNameLibraryFilterValues();
  const houseHoldNames = useHouseHoldNameLibraryFilterValues();
  const programs = useProgramLibraryFilterValues();

  const location = useLocation<PortfoliosLocationState>();
  const history = useHistory();
  const derivedInitialFilterState: FilterState = location.state?.status
    ? { ...initialFilterState, status: location.state?.status }
    : initialFilterState;

  const [filterState, dispatchBase] = useReducer((state: FilterState, action: FilterAction) => {
    switch (action.type) {
      case 'set':
        return { ...state, [action.key]: Array.isArray(action.values) ? action.values : [action.values] };
      case 'remove':
        return { ...state, [action.key]: state[action.key].filter((v) => v !== action.value) };
      case 'clearAll':
        return emptyFilterState;
      default:
        return state;
    }
  }, derivedInitialFilterState);

  const dispatch = useCallback(
    (action: FilterAction) => {
      setPages(1);
      dispatchBase(action);
    },
    [dispatchBase],
  );

  useEffect(() => {
    if (location.state?.status) {
      dispatch({ type: 'set', key: 'status', values: location.state?.status });
      history.replace({
        ...location,
        state: { ...location.state, status: undefined },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.status, dispatch]);

  const { total, results, isLoading } = usePortfoliosLibraryData(
    pages,
    search,
    filterState.household,
    filterState.client,
    filterState.program,
    filterState.status,
    filterState.taxStatus,
    sortSettings,
  );

  const colDefs = usePortfoliosLibraryColDefs();

  const gridRef = useRef<AgGridReact>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);

  const onGridReady = useCallback((gridReadyEvent: GridReadyEvent) => {
    const { api } = gridReadyEvent;
    api.setColumnsPinned(['ag-Grid-ControlsColumn', 'name'], 'left');
  }, []);

  const onCellClicked = useCallback((event: CellClickedEvent) => {
    if (event.column?.getColId() !== 'actions') {
      // disable selection for menu column
      const previousSelection = event.node.isSelected();
      event.node.setSelected(!previousSelection);
    }
  }, []);

  /* region onColumnResized */

  const throttledOnColumnResizedRef = useRef(
    throttle(() => {
      if (!gridWrapperRef.current) {
        return;
      }

      const headerViewport = gridWrapperRef.current.querySelector('.ag-header-viewport');
      if (!headerViewport) {
        return;
      }

      const isOverflowing = headerViewport.scrollWidth > headerViewport.clientWidth;
      gridWrapperRef.current.classList.toggle(viewportIsOverflowingClassName, isOverflowing);
    }, 100),
  );

  const onColumnResized = useCallback(() => {
    throttledOnColumnResizedRef.current();
  }, []);

  const clearAllFilters = () => dispatch({ type: 'clearAll' });

  const hasActiveFilters = Object.values(filterState).some((arr) => arr.length > 0);

  const onSearch = useCallback((newSearch: string) => {
    setSearch(newSearch);
    setPages(1);
  }, []);

  const [selectedRows, setSelectedRows] = useState<LibrarySearchEntity[]>([]);

  const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    event.api.refreshHeader();
    const selectedRows = event.api.getSelectedRows() as LibrarySearchEntity[];
    setSelectedRows(selectedRows);
  }, []);

  const onSortChanged = useCallback((event: SortChangedEvent) => {
    const sortedColumns = event.api.getColumnState().filter((s) => s.sort);
    if (sortedColumns.length === 0) {
      setSortSettings(undefined);
    } else {
      const sortedColumn = sortedColumns[0]!;
      setSortSettings({
        field: sortedColumn.colId,
        order: sortedColumn.sort ?? 'asc',
      });
    }
  }, []);

  const bottomInfoRowRenderer = useCallback(
    () =>
      !isLoading && (
        <div className="py-[8px] text-[12px] text-venn-grey-70">
          *These metrics are calculated using the overlapping date range of our {getTextThemeProvider().factorLensName},
          investment allocation date ranges, and the available timeseries for the investment or portfolio.
        </div>
      ),
    [isLoading],
  );

  const isInitialLoad = isLoading && pages === 1;
  const isMorePages = Math.ceil(total / TABLE_PAGE_SIZE) > pages;
  const pageLimitReached = pages >= PAGE_LIMIT;

  return (
    <PortfoliosLibraryStyledWrapper className={cn('pb-[20px]')}>
      <div className={cn('sticky grid top-0 bg-white z-[4]')}>
        <div className={cn('flex gap-[30px] border-b border-venn-grey')}>
          <div className={cn('flex w-full')}>
            <div
              className={cn('flex flex-initial items-center gap-[10px] py-[20px] px-[40px] border-r border-venn-grey')}
              style={{ borderRightStyle: 'solid' }}
            >
              <div
                className={cn('w-[56px] h-[56px] bg-center bg-cover')}
                style={{
                  backgroundImage: Images.rcm && `url(${Images.rcm})`,
                }}
              />
              <div>
                <div className={cn('font-bold')}>Welcome</div>
                <div className={cn('text-[32px]', 'font-light')}>{profileSettings?.displayName}</div>
              </div>
            </div>
            <div className={cn('flex-1 items-center px-[40px] py-[12px]')}>
              <div className={cn('flex')}>
                <PortfoliosTypeaheadSearchBar
                  searchText={search}
                  onSearchTextChange={onSearch}
                  placeholder="Search library by Portfolio, Household, Ownership, or Client"
                />
              </div>
              <div className={cn('flex gap-[6px] pt-[6px]')}>
                <SimpleFilter
                  applyLabel="Apply"
                  closeOnOnlyClick
                  items={(houseHoldNames.results ?? []).map((p) => ({ label: p, value: p }))}
                  initialSelected={filterState.household}
                  label="HouseHold"
                  onFilter={(values) => dispatch({ type: 'set', key: 'household', values })}
                  refreshedStyling
                  disabled={houseHoldNames.isLoading}
                  menuWidth={350}
                  isAdvanced
                  ignoreHierarchySelect
                />
                <SimpleFilter
                  applyLabel="Apply"
                  closeOnOnlyClick
                  items={(clientNames.results ?? []).map((p) => ({ label: p, value: p }))}
                  initialSelected={filterState.client}
                  label="Client"
                  onFilter={(values) => dispatch({ type: 'set', key: 'client', values })}
                  refreshedStyling
                  disabled={clientNames.isLoading}
                  menuWidth={250}
                  isAdvanced
                  ignoreHierarchySelect
                />
                <SimpleFilter
                  applyLabel="Apply"
                  closeOnOnlyClick
                  items={(programs.results ?? []).map((p) => ({ label: p, value: p }))}
                  initialSelected={filterState.program}
                  label="Program"
                  onFilter={(values) => dispatch({ type: 'set', key: 'program', values })}
                  refreshedStyling
                  disabled={programs.isLoading}
                  menuWidth={300}
                  isAdvanced
                  ignoreHierarchySelect
                />
                <SimpleFilter
                  applyLabel="Apply"
                  closeOnOnlyClick
                  items={PORTFOLIO_STATUSES}
                  initialSelected={filterState.status}
                  label="Account Status"
                  onFilter={(values) => dispatch({ type: 'set', key: 'status', values })}
                  refreshedStyling
                  isAdvanced
                  fullWidth
                />
                {hasTaxImpact && (
                  <SimpleFilter
                    applyLabel="Apply"
                    closeOnOnlyClick
                    items={TAX_STATUSES}
                    initialSelected={filterState.taxStatus}
                    label="Tax Status"
                    onFilter={(values) => dispatch({ type: 'set', key: 'taxStatus', values })}
                    refreshedStyling
                    isAdvanced
                    fullWidth
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={cn('px-[40px]')}>
        <div className={cn('flex items-center justify-between py-[5px] pl-[10px] border-b border-venn-grey')}>
          <PortfoliosTableAnnotation
            bulkActionsComponent={bulkActionsComponent}
            userSelectedRowsCount={0}
            totalRowsCount={total}
            clearAllFilters={clearAllFilters}
            hasActiveFilters={hasActiveFilters}
          >
            {[
              { key: 'household', pills: filterState.household.map((h) => ({ name: h, value: h })) },
              { key: 'client', pills: filterState.client.map((h) => ({ name: h, value: h })) },
              { key: 'program', pills: filterState.program.map((h) => ({ name: h, value: h })) },
              {
                key: 'status',
                pills: getPillDataFromFilterOptions(filterState.status, PORTFOLIO_STATUSES),
              },
              {
                key: 'taxStatus',
                pills: getPillDataFromFilterOptions(filterState.taxStatus, TAX_STATUSES),
              },
            ].map(({ key, pills }) => (
              <FilterPillGroup key={key} pills={pills} dispatch={dispatch} prefix={key} />
            ))}
          </PortfoliosTableAnnotation>
        </div>

        <PortfoliosTableActions selectedRows={selectedRows} />

        <ConditionalOverlay condition={isInitialLoad}>
          <DataGridStyledWrapper ref={gridWrapperRef}>
            <DataGrid
              columnDefs={colDefs}
              gridRef={gridRef}
              defaultColDef={DEFAULT_COL_DEF}
              domLayout="normal"
              rowStyle={rowStyle}
              getRowId={getRowId}
              headerHeight={35}
              rowHeight={35}
              rowClassRules={rowClassRules}
              horizontalScrollbarHeight={15}
              rowData={results}
              rowSelection={rowSelection}
              suppressContextMenu
              onGridReady={onGridReady}
              onCellClicked={onCellClicked}
              onColumnResized={onColumnResized}
              onSelectionChanged={onSelectionChanged}
              onSortChanged={onSortChanged}
              serverSideSortOnServer
              pinnedBottomRowData={[
                {
                  /* Do not remove, needed to render info row */
                },
              ]}
              fullWidthCellRenderer={bottomInfoRowRenderer}
              gridOptions={{
                isFullWidthRow: (rowNode) => rowNode.rowNode.isRowPinned(),
              }}
            />
          </DataGridStyledWrapper>
        </ConditionalOverlay>
        <div className={cn('w-full mt-[16px] mb-[80px] flex justify-center')}>
          {!isInitialLoad && isMorePages && (
            <Button
              disabled={isLoading || pageLimitReached}
              refreshedStyling
              type="button"
              onClick={() => setPages((current) => current + 1)}
            >
              <span className={cn('flex items-end justify-center gap-[4px] h-[16px] w-[145px]')}>
                {isLoading ? (
                  <Spinner refreshedStyling size={LoadingSize.nano} />
                ) : pageLimitReached ? (
                  'Maximum reached'
                ) : (
                  `Load ${TABLE_PAGE_SIZE} more results`
                )}
              </span>
            </Button>
          )}
        </div>
      </div>
    </PortfoliosLibraryStyledWrapper>
  );
};

export default withRouter(PortfoliosLibrary);

const PortfoliosLibraryStyledWrapper = styled.div`
  // Fix Tailwind due to preflight: false
  .border-b {
    border-bottom-style: solid;
  }
`;

const DataGridStyledWrapper = styled.div`
  --box-shadow-left: 4px 0px 10px 0px #00000026;
  --box-shadow-right: -4px 0px 10px 0px #00000026;

  // Fix AgGrid height
  .ag-root-wrapper-body.ag-layout-normal {
    height: 100% !important;
    min-height: 500px;
  }

  .ag-root {
    --ag-cell-horizontal-padding: 10px;
    --ag-border-color: ${GetColor.GreyScale.Grey40};
    --ag-row-hover-color: ${ColorUtils.hex2rgbaFrom(GetColor.DeepDarkBlue, 0.1)};
    --ag-odd-row-background-color: ${GetColor.GreyScale.Grey10};

    --ag-checkbox-checked-color: ${GetColor.DarkBlue};
    --ag-checkbox-indeterminate-color: ${GetColor.DarkBlue};
  }

  .ag-header {
    background-color: ${GetColor.GreyScale.Grey20};
  }

  .ag-header-cell[col-id='actions'] {
    --ag-cell-horizontal-padding: 5px;
  }

  .ag-cell-label-container {
    padding: 0;
  }

  /* region Shadows between pinned columns */

  .ag-header-viewport,
  .ag-center-cols-viewport {
    display: flex;
    overscroll-behavior-x: contain;
    overscroll-behavior-y: auto;
  }

  .ag-header-container,
  .ag-center-cols-container {
    flex-shrink: 0;
    order: 3;
  }

  &.${viewportIsOverflowingClassName} {
    .ag-header-viewport::before,
    .ag-header-viewport::after,
    .ag-center-cols-viewport::before,
    .ag-center-cols-viewport::after {
      content: '';
      display: block;
      position: sticky;
      order: 1;
      width: 0;
      height: 100%;
      border-left: 25px solid #fff;
      inset: 0 auto 0 -25px;
      margin-left: -25px;
      z-index: ${ZIndex.Front};
      pointer-events: none;
      box-shadow: var(--box-shadow-left);
      flex-shrink: 0;
    }

    .ag-header-viewport::after,
    .ag-center-cols-viewport::after {
      position: sticky;
      order: 2;
      inset: 0 auto 0 100%;
      margin-left: 0;
      margin-right: -25px;
      box-shadow: var(--box-shadow-right);
    }
  }

  /* stylelint-disable property-no-unknown */
  @supports (animation-timeline: scroll(inline)) {
    .ag-header-viewport::before,
    .ag-header-viewport::after,
    .ag-center-cols-viewport::before,
    .ag-center-cols-viewport::after {
      animation-timeline: scroll(inline);
      animation-fill-mode: forwards;
    }

    .ag-header-viewport::before,
    .ag-center-cols-viewport::before {
      opacity: 0;
      animation-name: shadow-fade-in;
      animation-range: 0 25px;
    }

    .ag-header-viewport::after,
    .ag-center-cols-viewport::after {
      opacity: 1;
      animation-name: shadow-fade-out;
      animation-range: calc(100% - 25px) 100%;
    }

    @keyframes shadow-fade-in {
      to {
        opacity: 1;
      }
    }

    @keyframes shadow-fade-out {
      to {
        opacity: 0;
      }
    }
  }
  /* stylelint-enable property-no-unknown */

  /* endregion */
`;
